import app, { Component } from 'apprun';
import { showSaveStatusMessage } from '../utils';
import { speakerEventRating } from '../api';

export default class CommonPropertyComponent extends Component {
  dateFmt = 'DD-MMM-YYYY';

  state = {
    speakerEvents: [],
    selectedRatings: []
  };

  calculateAverageRating = (expertEventID: string, ratings: any[]) => {
    const eventRatings = ratings.filter(r => r.expertEventID.toString() === expertEventID);
    if (eventRatings.length === 0) {
      return 0;
    }
    const sum = eventRatings.reduce((acc, curr) => acc + curr.rating, 0);
    return parseFloat((sum / eventRatings.length).toFixed(1));
  };

  formatReviewDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  view = (state) => {
    if (!state.speakerEvents?.events?.length) {
      return app.createElement('div', { style: { width: '700px' } }, 'No events');
    }

    const renderEvent = (event) => {
      const eventRatings = state.speakerEvents.ratings
        .filter(r => r.expertEventID.toString() === event.expertEventID)
        .sort((a, b) => b.ratingID - a.ratingID);
      const avgRating = this.calculateAverageRating(event.expertEventID, state.speakerEvents.ratings);
      return [
        app.createElement('tr', {
          class: 'event-row',
          onclick: (e) => {
            if (!(e.target as HTMLElement).closest('.ratings-container')) {
              this.run('speakerEvent-edit', event, e);
            }
          }
        },
        app.createElement('td', null, event.expertEventDate),
        app.createElement('td', null, event.edLocationName),
        app.createElement('td', null, event.contactName),
        app.createElement('td', null, event.contactNumber),
        app.createElement('td', null, avgRating)
        ),
        ...(eventRatings.length > 0 ? [app.createElement('tr', { class: 'rating-details-row' },
          app.createElement('td', { style: { padding: '10px 0' }, colSpan: 5 },
            app.createElement('div', { style: { padding: '0 15px' } },
              app.createElement('table', { class: 'table table-bordered', style: { width: '100%', marginBottom: 0 } },
                [
                  app.createElement('thead', null,
                    app.createElement('tr', null, [
                      app.createElement('th', { style: { width: '25%', textAlign: 'center' } }, 'Review Date'),
                      app.createElement('th', { style: { width: '25%', textAlign: 'center' } }, 'Rating'),
                      app.createElement('th', { style: { width: '40%', textAlign: 'center' } }, 'Comments'),
                      app.createElement('th', { style: { width: '10%', textAlign: 'center' } }, 'Edit')
                    ])
                  ),
                  app.createElement('tbody', null,
                    eventRatings.map(rating =>
                      app.createElement('tr', null, [
                        app.createElement('td', { style: { width: '25%', textAlign: 'center' } }, this.formatReviewDate(rating.reviewDate)),
                        app.createElement('td', { style: { width: '25%', textAlign: 'center' } }, rating.rating),
                        app.createElement('td', { style: { width: '40%', textAlign: 'center' } }, rating.comments? rating.comments.length > 30? `${rating.comments.slice(0, 30)}...`: rating.comments: 'No comments'),
                        app.createElement('td', { style: { width: '10%', textAlign: 'center' } },
                          app.createElement('input', {
                            type: 'checkbox',
                            value: rating.ratingID,
                            checked: state.selectedRatings.includes(rating.ratingID.toString()),
                            onclick: (e) => {
                              e.stopPropagation();
                              this.run('toggleRating', rating.ratingID.toString());
                            }
                          })
                        )
                      ])
                    )
                  )
                ])
            )
          )
        )] : [app.createElement('tr', { class: 'rating-details-row' },
          app.createElement('td', { colSpan: 5, class: 'text-center' }, 'No ratings available')
        )])
      ];
    };

    const hasAnyRatings = state.speakerEvents.ratings && state.speakerEvents.ratings.length > 0;

    return app.createElement('div', { style: { width: '700px' } },
      hasAnyRatings && app.createElement('div', { style: { marginBottom: '10px' } },
        app.createElement('button', {
          class: 'btn btn-primary',
          style: { marginRight: '10px' },
          onclick: () => this.run('selectAll')
        }, 'Select All'),
        app.createElement('button', {
          class: 'btn btn-default',
          style: { marginRight: '10px' },
          onclick: () => this.run('clearAll')
        }, 'Clear All'),
        app.createElement('button', {
          class: 'btn btn-danger',
          onclick: () => this.run('deleteSelected')
        }, 'Delete')
      ),
      app.createElement('table', { class: 'table table-striped table-condensed table-bordered table-hover' },
        app.createElement('thead', null,
          app.createElement('tr', null,
            app.createElement('th', null, 'Event Date'),
            app.createElement('th', null, 'Location'),
            app.createElement('th', null, 'Contact Name'),
            app.createElement('th', null, 'Contact Number'),
            app.createElement('th', null, 'Avg Rating')
          )
        ),
        app.createElement('tbody', null,
          [...state.speakerEvents.events]
            .sort((a, b) => new Date(b.expertEventDate).getTime() - new Date(a.expertEventDate).getTime())
            .map(event => renderEvent(event))
            .flat()
        )
      )
    );
  };

  update = {
    clearAll: (state) => {
      return { ...state, selectedRatings: [] };
    },

    selectAll: (state) => {
      const allRatingIds = state.speakerEvents.ratings.map(r => r.ratingID.toString());
      return { ...state, selectedRatings: allRatingIds };
    },

    deleteSelected: async (state) => {
      if (state.selectedRatings.length === 0) {
        return state;
      }
      await speakerEventRating.deleteSpeakerEventRating(state.personId, {
        ratingIDs: state.selectedRatings
      });
      showSaveStatusMessage('ratings');
      return this.run('refresh', state);
    },

    refresh: async (state, newState) => {
      state.speakerEvents = await speakerEventRating.getSpeakerEventRating(newState.personId) || [];
      return { ...state, ...newState };
    },

    toggleRating: (state, ratingId) => {
      const selectedRatings = [...state.selectedRatings];
      const index = selectedRatings.indexOf(ratingId);
      if (index === -1) {
        selectedRatings.push(ratingId);
      } else {
        selectedRatings.splice(index, 1);
      }
      return { ...state, selectedRatings };
    },

  };

  rendered = (state) => {
    return { ...state, editingSpeakerEvent: false, eventDate: '' };
  };

  mounted = ({ state }) => {
    this.run('refresh', state);
  };
}
